<template>
  <div class="date-and-time-picker column">
    <div class="start-pickers">
      <DayPicker
        :is-editable="isEditable"
        :is-edited="isEdited.start_day"
        :date="startDay"
        @update:date="editStartDate"
      />
      <TimeStartPicker
        :event-time="eventTime"
        :is-editable="isEditable"
        :is-edited="isEdited.start_time"
        :locale="locale"
        @update:start-time="editStart"
      />
      <DurationPicker
        :duration-minutes="durationMinutes"
        :is-all-day="isAllDay"
        :is-editable="isEditable"
        @update:duration="editDuration"
      />
    </div>
    <div class="end-pickers">
      <DayPicker
        :is-editable="isEditable"
        :is-edited="isEdited.end_day"
        :date="endDay"
        @update:date="editEndDate"
      />
      <TimeEndPicker
        :event-time="eventTime"
        :is-editable="isEditable"
        :is-edited="isEdited.end_time"
        :is-compact="isCompact"
        :is-end-shown="isEndShown"
        :is-different-day-pickable="isDifferentDayPickable"
        :is-end-different-from-start="isEndDifferentFromStart"
        :locale="locale"
        @update:end-time="editEnd"
      />
    </div>
    <BaseButton
      v-if="isClosable && isEditable"
      class="close"
      kind="auxiliary"
      @click="$emit('close')"
    >
      <template #icon>
        <CloseIcon />
      </template>
    </BaseButton>
  </div>
</template>
<script lang="ts">
import {
  addDays,
  addMinutes,
  differenceInDays,
  isBefore,
  isEqual,
  isSameDay,
  startOfDay,
  subMinutes,
} from "date-fns";
import { computed, defineComponent, PropType, ref, toRefs } from "vue";

import { Daytime, EventTime, MandateDates } from "@ctrl/controller-rpc";

import BaseButton from "@/components/base/BaseButton.vue";
import { getDuration } from "@/event";

import CloseIcon from "../icons/CloseIcon.vue";
import * as inject from "../inject";
import type { TimeEdited } from "../planner/controllers/event";
import DayPicker from "./DayPicker.vue";
import DurationPicker from "./DurationPicker.vue";
import TimeEndPicker from "./TimeEndPicker.vue";
import TimeStartPicker from "./TimeStartPicker.vue";

export default defineComponent({
  components: {
    BaseButton,
    CloseIcon,
    DayPicker,
    DurationPicker,
    TimeEndPicker,
    TimeStartPicker,
  },
  props: {
    eventTime: { type: Object as PropType<EventTime>, required: true },
    isClosable: { type: Boolean, default: false },
    isCompact: { type: Boolean, default: false },
    isDifferentDayPickable: { type: Boolean, required: false, default: true },
    isEditable: { type: Boolean, default: false },
    isEdited: {
      type: Object as PropType<TimeEdited>,
      default: () => ({
        time: false,
        start_day: false,
        start_time: false,
        end_time: false,
        end_day: false,
      }),
    },
  },
  emits: {
    close: () => true,
    "update:eventTime": (_date: EventTime) => true,
  },
  setup(props, { emit }) {
    const { isEditable, eventTime } = toRefs(props);
    const locale = inject.locale();
    const editStart = (daytime: Daytime) => {
      if (isAllDay.value) {
        toggleAllDay(daytime);
      } else if (
        "start_time" in eventTime.value &&
        "end_time" in eventTime.value
      ) {
        const start_time = new Date(eventTime.value.start_time);
        start_time.setHours(daytime.hours);
        start_time.setMinutes(daytime.minutes || 0);
        const duration = getDuration(
          eventTime.value.start_time,
          eventTime.value.end_time
        );
        emit("update:eventTime", {
          start_time,
          end_time: addMinutes(start_time, duration),
        });
      }
    };
    const editEnd = (daytime: Daytime) => {
      if ("start_time" in eventTime.value && "end_time" in eventTime.value) {
        const end_time = new Date(eventTime.value.end_time);
        end_time.setHours(daytime.hours, daytime.minutes || 0);
        if (
          isBefore(end_time, eventTime.value.start_time) ||
          isEqual(end_time, eventTime.value.start_time)
        ) {
          const duration = getDuration(
            eventTime.value.start_time,
            eventTime.value.end_time
          );
          emit("update:eventTime", {
            start_time: subMinutes(end_time, duration),
            end_time,
          });
        } else {
          emit("update:eventTime", {
            start_time: eventTime.value.start_time,
            end_time,
          });
        }
      }
    };
    const editStartDate = (dates: MandateDates) => {
      if (dates.type !== "date") return;
      if ("start_time" in eventTime.value && "end_time" in eventTime.value) {
        const start_time = new Date(dates.date);
        start_time.setHours(
          eventTime.value.start_time.getHours(),
          eventTime.value.start_time.getMinutes()
        );
        const durationMs =
          eventTime.value.end_time.getTime() -
          eventTime.value.start_time.getTime();
        const end_time = new Date(start_time.getTime() + durationMs);
        emit("update:eventTime", {
          start_time,
          end_time,
        });
      } else if (
        "start_date" in eventTime.value &&
        "end_date" in eventTime.value
      ) {
        const start_date = startOfDay(dates.date);
        emit("update:eventTime", {
          start_date: startOfDay(dates.date),
          end_date: addDays(
            eventTime.value.end_date,
            differenceInDays(start_date, eventTime.value.start_date)
          ),
        });
      } else if ("date" in eventTime.value) {
        emit("update:eventTime", { date: startOfDay(dates.date) });
      }
    };
    const editEndDate = (dates: MandateDates) => {
      if (dates.type !== "date") return;
      if ("start_time" in eventTime.value && "end_time" in eventTime.value) {
        const end_time = new Date(dates.date);
        end_time.setHours(
          eventTime.value.end_time.getHours(),
          eventTime.value.end_time.getMinutes()
        );
        emit("update:eventTime", {
          start_time:
            eventTime.value.start_time <= end_time
              ? eventTime.value.start_time
              : addMinutes(end_time, -30),
          end_time,
        });
      } else if ("start_date" in eventTime.value || "date" in eventTime.value) {
        const start_date =
          "start_date" in eventTime.value
            ? eventTime.value.start_date
            : eventTime.value.date;
        const end_date = startOfDay(dates.date);
        emit("update:eventTime", {
          start_date: start_date <= end_date ? start_date : end_date,
          end_date,
        });
      }
    };
    const toggleAllDay = (daytime?: Daytime) => {
      if (!isEditable.value) return;
      if ("start_time" in eventTime.value && "end_time" in eventTime.value) {
        if (isSameDay(eventTime.value.start_time, eventTime.value.end_time)) {
          emit("update:eventTime", {
            date: startOfDay(eventTime.value.start_time),
          });
        } else {
          emit("update:eventTime", {
            start_date: startOfDay(eventTime.value.start_time),
            end_date: startOfDay(eventTime.value.end_time),
          });
        }
      } else if (
        ("start_date" in eventTime.value && "end_date" in eventTime.value) ||
        "date" in eventTime.value
      ) {
        const start_time =
          "start_date" in eventTime.value
            ? eventTime.value.start_date
            : eventTime.value.date;
        start_time.setHours(daytime ? daytime.hours : 10);
        start_time.setMinutes(daytime && daytime.minutes ? daytime.minutes : 0);
        emit("update:eventTime", {
          start_time,
          end_time: addMinutes(start_time, 30),
        });
      }
    };
    const isAllDay = computed(() => !("start_time" in eventTime.value));
    const durationMinutes = computed<number | undefined>(() => {
      if ("start_time" in eventTime.value && "end_time" in eventTime.value) {
        const dur = getDuration(
          eventTime.value.start_time,
          eventTime.value.end_time
        );
        return dur >= 0 ? dur : undefined;
      }
      return undefined;
    });
    const editDuration = (minutes: number) => {
      if ("start_time" in eventTime.value) {
        emit("update:eventTime", {
          start_time: eventTime.value.start_time,
          end_time: addMinutes(eventTime.value.start_time, minutes),
        });
      }
    };
    const isEndShown = ref(false);
    const startDay = computed<Date>(() => {
      const time = eventTime.value;
      // @ts-ignore
      return time.start_time || time.start_date || time.date;
    });
    const endDay = computed<Date>(() => {
      const time = eventTime.value;
      // @ts-ignore
      return time.end_time || time.end_date || time.date;
    });
    const isEndDifferentFromStart = computed<boolean>(() => {
      const t = eventTime.value;
      return (
        ("start_time" in t &&
          "end_time" in t &&
          !isSameDay(t.start_time, t.end_time)) ||
        ("start_date" in t &&
          "end_date" in t &&
          !isSameDay(t.start_date, t.end_date))
      );
    });
    return {
      durationMinutes,
      editDuration,
      editEnd,
      editEndDate,
      editStart,
      editStartDate,
      endDay,
      isAllDay,
      isEndDifferentFromStart,
      isEndShown,
      locale,
      startDay,
    };
  },
});
</script>

<style scoped lang="scss">
.date-and-time-picker {
  display: flex;

  &:not(.column) {
    flex-direction: row;
    align-items: center;
  }
  &.column {
    flex-direction: column;
    .end-pickers {
      padding-top: 4px;
    }
  }

  .start-pickers,
  .end-pickers {
    display: flex;
    flex-direction: row;
  }

  &:hover .close {
    display: inherit;
  }
  .close {
    display: none;
    height: 23px;
    margin-inline-start: 8px;
    .icon {
      height: 10px;
      width: 10px;
    }
  }
}
</style>
